import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, CalendarClock, FileText, Scale, ShieldCheck } from "lucide-react";
import {
  DEFAULT_PUBLIC_UI_CONFIG,
  fetchPublicUiConfig,
  normalizePublicUiConfig,
} from "../../utils/publicUiConfig";
import PublicPageWidgets from "../PublicPageWidgets";
import { PublicHeader, PublicQuickLinks } from "../../pages/CampusConnect";
import SectionBlock from "./SectionBlock";
import "../../styles/CampusPublicChrome.css";
import "../../styles/LegalCenter.css";

export const LEGAL_LAST_UPDATED = "October 2026";

const LEGAL_PAGES = [
  { id: "terms", label: "Terms & Conditions", path: "/terms", icon: Scale },
  { id: "license", label: "Software License", path: "/license", icon: FileText },
  { id: "policies", label: "Policies", path: "/policies", icon: ShieldCheck },
];

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/* Shared shell for /terms, /license and /policies: public Campus Connect header and footer,
   hero, Legal Center tabs, a section navigator and the numbered sections. */
export default function LegalLayout({ current, title, subtitle, sections, children }) {
  const navigate = useNavigate();
  const [publicConfig, setPublicConfig] = useState(() => normalizePublicUiConfig(DEFAULT_PUBLIC_UI_CONFIG));
  const [activeId, setActiveId] = useState(sections[0]?.id);

  useEffect(() => {
    let active = true;
    fetchPublicUiConfig().then((value) => { if (active) setPublicConfig(value); }).catch(() => {});
    return () => { active = false; };
  }, []);

  // Arriving from another page: start at the top (or at the linked section).
  useEffect(() => {
    const target = window.location.hash && document.getElementById(window.location.hash.slice(1));
    if (target) target.scrollIntoView();
    else window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        if (visible.length) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-18% 0px -70% 0px" },
    );
    sections.forEach((section) => {
      const node = document.getElementById(section.id);
      if (node) observer.observe(node);
    });
    return () => observer.disconnect();
  }, [sections]);

  const openItem = (item) => {
    const destination = item?.destination || item?.href;
    if (destination?.startsWith("/")) navigate(destination);
    else if (/^https?:\/\//i.test(destination || "")) window.open(destination, "_blank", "noopener,noreferrer");
    else if (item?.action === "home" || item?.action === "about") navigate("/");
  };

  const jumpTo = (event, id) => {
    const node = document.getElementById(id);
    if (!node) return;
    event.preventDefault();
    node.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" });
    window.history.replaceState(null, "", `#${id}`);
    setActiveId(id);
  };

  return (
    <div className="lc-page">
      <PublicHeader config={publicConfig} onOpen={openItem} applications={publicConfig.selector?.cards || []} />

      <main>
        <section className="lc-hero">
          <div className="lc-hero-inner">
            <p className="lc-eyebrow"><Building2 size={15} aria-hidden="true" />Campus Connect Legal Center</p>
            <h1>{title}</h1>
            <p className="lc-hero-sub">{subtitle}</p>
            <div className="lc-meta">
              <span>Thapar Campus Connect · Office of Student Affairs · Thapar Institute of Engineering &amp; Technology</span>
              <span className="lc-updated"><CalendarClock size={14} aria-hidden="true" />Last updated: {LEGAL_LAST_UPDATED}</span>
            </div>
          </div>

          <nav className="lc-tabs" aria-label="Legal Center">
            {LEGAL_PAGES.map(({ id, label, path, icon: Icon }) => (
              <Link key={id} to={path} className="lc-tab" aria-current={current === id ? "page" : undefined}>
                <Icon size={16} aria-hidden="true" />{label}
              </Link>
            ))}
          </nav>
        </section>

        <div className="lc-body">
          <aside className="lc-toc">
            <nav aria-label="On this page">
              <p className="lc-toc-title">On this page</p>
              <ol>
                {sections.map((section, index) => (
                  <li key={section.id}>
                    <a href={`#${section.id}`} onClick={(event) => jumpTo(event, section.id)} aria-current={activeId === section.id ? "true" : undefined}>
                      <span>{index + 1}</span>{section.title}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </aside>

          <div className="lc-content">
            {sections.map((section, index) => (
              <SectionBlock key={section.id} id={section.id} number={index + 1} icon={section.icon} title={section.title}>
                {section.body}
              </SectionBlock>
            ))}
            {children}
          </div>
        </div>
      </main>

      <PublicQuickLinks config={publicConfig} onOpen={openItem} />
      <PublicPageWidgets hideFooter />
    </div>
  );
}
